// @ts-strict-ignore
import { ActionType, MatrixModelDataType } from 'flow/store/engine/constants';
import type {
  Action,
  BooleanRule,
  BooleanRuleArray,
  IndexedRule,
  PopulatedThreshold,
  ReasonCodeGroup,
  RuleObject,
  Rules,
  SchemaInstance,
  Services,
  ServicesByName,
  Threshold,
  ThresholdType,
  WorkflowProps,
} from 'flow/store/engine/types';
import type { FeatureFlags } from '~/helpers/typescript';
import { getReasonCodeGroupName } from 'flow/service/reasonCodes';
import { getThresholdOperatorString } from 'flow/editor/components/thresholds/operators';

import { extractValuesByKey } from '../util';
import { getActionName, isActionOfType } from '../actions';
import {
  BEST_EFFORT_PARSING_DEFAULT_STRING,
  BEST_EFFORT_PARSING_PLACEHOLDER_MULTISELECT,
  BEST_EFFORT_PARSING_PLACEHOLDER_STRING,
  PLACEHOLDER_STRING,
  supportedNullActionTypes,
} from './constants';


export function getAllThresholdFromRules({
  rules,
  thresholds,
}: {
  rules: Rules;
  thresholds: Record<string, Threshold>;
}) {
  return extractThresholdIds(rules)
    .map(id => thresholds[id])
    // filter out any `undefined` values coming from non-found thresholds
    .filter(Boolean);
}

export function isRuleObjectIndexedRule(
  ruleObject: RuleObject,
): ruleObject is IndexedRule {
  return Object.hasOwn(ruleObject, 'id');
}

export function convertThresholdToString({
  actions,
  reasonCodeGroups,
  services,
  threshold,
  featureFlags,
}: {
  actions: SchemaInstance['actions'];
  reasonCodeGroups: ReasonCodeGroup[];
  services: ServicesByName;
  threshold: Threshold;
  featureFlags: Partial<FeatureFlags>;
}): string {
  const leftAction = actions[threshold.left.actionId];
  const rightAction = actions[threshold.right.actionId];
  let leftActionName = getActionName(leftAction);
  let leftAttribute = null;
  let rightValue: number | string = threshold.right.leftTagActionResult ? 'set' : 'not set';

  if (rightAction) {
    // for dynamic threshold types
    rightValue = getActionName(rightAction);

    if (isActionOfType(rightAction, ActionType.SERVICE)) {
      const { internalName } = rightAction.service;
      const { right: { attributeId } } = threshold;
      rightValue += `: ${services[internalName]?.attributes[attributeId]?.display_name}`;
    }
  } else {
    // for static threshold types
    switch (leftAction.type) {
      case ActionType.SERVICE: {
        const { internalName } = leftAction.service;
        const { left: { attributeId } } = threshold;
        leftAttribute = services[internalName].attributes[attributeId];
        leftActionName += `:${leftAttribute.attribute_name}`;
        if (leftAttribute.dataType === 'reasoncodes') {
          rightValue = getReasonCodeGroupName(threshold.right.reasonCodeGroupId, reasonCodeGroups);
        } else {
          rightValue = getThresholdRightValueBasedOnBestEffortParsing({
            actions,
            reasonCodeGroups,
            services,
            threshold,
            featureFlags,
          });
        }
        break;
      }
      case ActionType.INPUT: {
        rightValue = getThresholdRightValueBasedOnBestEffortParsing({
          actions,
          reasonCodeGroups,
          services,
          threshold,
          featureFlags,
        });
        break;
      }
      case ActionType.OUTPUT:
      case ActionType.MATRIX_MODEL:
      case ActionType.IMPORTED_MATRIX_MODEL:
      case ActionType.IMPORTED_OUTPUT_ATTRIBUTE: {
        rightValue = threshold.right.attributeValue === ''
          ? PLACEHOLDER_STRING : threshold.right.attributeValue;
        break;
      }
      case ActionType.OUTCOME_REASON: {
        rightValue = threshold.right.attributeValue === 'true' ? 'set' : 'not set';
        break;
      }
    }
  }
  const operator = getThresholdOperatorString({
    action: leftAction,
    attribute: leftAttribute,
    operator: threshold.operator,
  });
  return `${leftActionName} ${operator} ${rightValue}`;
}


export function buildRulesString({
  actions,
  reasonCodeGroups,
  rules,
  ruleStr = '',
  services,
  thresholds,
  featureFlags,
}: {
  actions: SchemaInstance['actions'];
  reasonCodeGroups: ReasonCodeGroup[];
  rules: Rules;
  ruleStr?: string;
  services: ServicesByName;
  thresholds: SchemaInstance['thresholds'];
  featureFlags: Partial<FeatureFlags>;
}): string {
  if (typeof rules === 'object') {
    if ('id' in rules) {
      return convertThresholdToString({
        actions, reasonCodeGroups, services, threshold: thresholds[rules.id], featureFlags,
      });
    }
    if ('and' in rules) {
      const nestedRuleStr = (rules as BooleanRule).and
        .map(rule => buildRulesString({
          actions, reasonCodeGroups, rules: rule, ruleStr, services, thresholds, featureFlags,
        }))
        .join(`
  and `);
      return `( ${nestedRuleStr} )`;
    }
    if ('or' in rules) {
      const nestedRuleStr = (rules as { or: IndexedRule[] }).or
        .map(rule => buildRulesString({
          actions, reasonCodeGroups, rules: rule, ruleStr, services, thresholds, featureFlags,
        }))
        .join(`
  or `);
      return `( ${nestedRuleStr} )`;
    }
  }
  return '';
}


export function convertRuleToString(opts: {
  actions: SchemaInstance['actions'];
  reasonCodeGroups: ReasonCodeGroup[];
  rules: Rules;
  services: ServicesByName;
  thresholds: SchemaInstance['thresholds'];
  featureFlags: Partial<FeatureFlags>;
}): string {
  const {
    actions,
    reasonCodeGroups,
    rules,
    services,
    thresholds,
    featureFlags,
  } = opts;

  const rulesAsString = buildRulesString({
    actions,
    reasonCodeGroups,
    rules,
    services,
    thresholds,
    featureFlags,
  });

  if (rulesAsString.startsWith('( ')) {
    const formattedRulesAsString = rulesAsString
      // remove an extra '( ' and ' )' that's exists at the around the buildRulesString result
      .slice(2, -2)
      // replaces all line breaks (\n) and any multiple spaces (\s{2,}) with a single space
      .replaceAll(/\s{2,}|\n/g, ' ');

    return formattedRulesAsString;
  }

  return rulesAsString;
}


/**
 * Accepts optional arguments for a specific action and/or attribute and tries
 * to pull the objects for action, service, and service attribute if it can, returning null if it
 * can't
 */
export function getActionServiceAttribute({
  actions,
  actionId,
  attributeId,
  services,
}: {
  actions: SchemaInstance['actions'];
  actionId: string | null;
  attributeId: number | null;
  services: WorkflowProps['services'];
}) {
  const action = actions[actionId] || null;
  const service = (
    action
    && action.type === ActionType.SERVICE
    && services[action.service.internalName]
  ) || null;
  const attribute = (
    attributeId
    && service
    && service.attributes[attributeId]
  ) || null;
  return { action, attribute, service };
}


export function getThresholdType(threshold: Threshold): ThresholdType {
  return threshold.right.actionId !== null ? 'dynamic' : 'static';
}

export function getThresholdLeftDataType(threshold: PopulatedThreshold): string | null {
  if (threshold.left.attribute?.dataType) {
    return threshold.left.attribute?.dataType;
  }

  /**
   * Excludes a check for `ActionTypes.OUTCOME_REASON` because we want those to be
   * treated the same as tags, but they technically have a `dataType` of `boolean`
   */
  if (threshold.left.action) {
    const leftAction = threshold.left.action;

    if (leftAction.type === ActionType.MATRIX_MODEL) {
      /**
        * Matrix models have a `matrix_data_type` of either `string` or `number` but for threshold
        * purposes the `number` types will always be parsed as `decimal` on the back end so that
        * is how we set the data type for the threshold here
        */
      return leftAction.matrixModel.matrixDataType === MatrixModelDataType.STRING
        ? leftAction.matrixModel.matrixDataType
        : 'decimal';
    }

    if (leftAction.type === ActionType.IMPORTED_MATRIX_MODEL) {
      return leftAction.importedMatrixModel.dataType;
    }

    if (leftAction.type === ActionType.IMPORTED_OUTPUT_ATTRIBUTE) {
      return leftAction.importedOutputAttribute.dataType;
    }

    return leftAction[leftAction.type]?.dataType;
  }

  return null;
}

/**
 * Accepts a threshold as it exists in state and populates it with the objects of various
 * things it might be referencing from other parts of the system, including actions,
 * services, service attributes, reason code groups
 */
export function populateThresholdData({
  actions,
  reasonCodeGroups,
  services,
  threshold,
}: {
  actions: SchemaInstance['actions'];
  reasonCodeGroups: WorkflowProps['reasonCodeGroups'];
  services: WorkflowProps['services'];
  threshold: Threshold;
}) {
  return {
    ...threshold,
    left: {
      ...getActionServiceAttribute({
        actions,
        actionId: threshold.left.actionId,
        attributeId: threshold.left.attributeId,
        services,
      }),
      ...threshold.left,
    },
    right: {
      ...getActionServiceAttribute({
        actions,
        actionId: threshold.right.actionId,
        attributeId: threshold.right.attributeId,
        services,
      }),
      ...threshold.right,
      reasonCodeGroup: threshold.right.reasonCodeGroupId
        && reasonCodeGroups.find(({ id }) => id === threshold.right.reasonCodeGroupId),
    },
  };
}

export function extractThresholdIds(
  actionRules: SchemaInstance['actions'][string]['rules'],
): string[] {
  const THRESHOLD_ID_KEY = 'id';
  const thresholdIds = extractValuesByKey(actionRules, THRESHOLD_ID_KEY);
  return thresholdIds;
}

export function checkIfRuleArrayHasAmbiguousKey(
  rules: (BooleanRule | IndexedRule)[],
) {
  return rules.filter(rule => {
    if (rule) {
      let ruleOperator;
      if ('and' in rule) {
        ruleOperator = 'and';
      } else if ('or' in rule) {
        ruleOperator = 'or';
      }

      if (ruleOperator && !('ambiguous' in rule)) {
        return checkIfRuleHasAmbiguousKey(rule[ruleOperator]);
      }

      if (rule.ambiguous) return true;
    }

    return false;
  });
}

export function checkIfRuleHasAmbiguousKey(
  rules: {} | IndexedRule | BooleanRule |
  BooleanRule[] | IndexedRule[] | (BooleanRule | IndexedRule)[],
) {
  if (!rules || Object.values(rules)?.length === 0) return false;
  if ('id' in rules) return false;

  let topLevelOperator;
  if ('and' in rules) {
    topLevelOperator = 'and';
  } else if ('or' in rules) {
    topLevelOperator = 'or';
  }

  const ambiguousKey = 'ambiguous' in rules;
  if (topLevelOperator && !ambiguousKey) {
    return checkIfRuleArrayHasAmbiguousKey(rules[topLevelOperator])?.length > 0;
  }

  if (ambiguousKey) return true;
  return checkIfRuleArrayHasAmbiguousKey(rules as (BooleanRule | IndexedRule)[])?.length > 0;
}

export function correctAmbiguousBooleanRuleObject(
  rules: BooleanRule,
  newRuleOperatorForCorrection: 'and' | 'or',
) {
  let topLevelOperator;
  if ('and' in rules) {
    topLevelOperator = 'and';
  } else if ('or' in rules) {
    topLevelOperator = 'or';
  }

  if (topLevelOperator && rules[topLevelOperator]) {
    const { result, arrayContainingAmbiguousOperator } = correctAmbiguousBooleanRuleArray(
      rules[topLevelOperator], newRuleOperatorForCorrection,
    );
    if (rules.ambiguous || arrayContainingAmbiguousOperator) {
      rules[newRuleOperatorForCorrection] = result;
      if (newRuleOperatorForCorrection !== topLevelOperator) delete rules[topLevelOperator];
    } else {
      rules[topLevelOperator] = result;
    }
  }

  return rules;
}

export function correctAmbiguousBooleanRuleArray(
  rules: BooleanRuleArray,
  newRuleOperatorForCorrection: 'and' | 'or',
) {
  let arrayContainingAmbiguousOperator;
  const result = rules.flatMap(rule => {
    let ruleOperator;
    if ('and' in rule) {
      ruleOperator = 'and';
    } else if ('or' in rule) {
      ruleOperator = 'or';
    }

    if (ruleOperator) {
      if (rule.ambiguous) {
        arrayContainingAmbiguousOperator = true;
        return rule.ambiguousGroup
          ? { [ruleOperator]: rule[ruleOperator] }
          : rule[ruleOperator];
      }
      return correctAmbiguousBooleanRuleObject(rule, newRuleOperatorForCorrection);
    }

    return rule;
  });

  return { result, arrayContainingAmbiguousOperator };
}

export function isSelfReferencingThreshold(threshold: Threshold) {
  const { actionId } = threshold;
  return actionId === threshold.left.actionId || actionId === threshold.right.actionId;
}

export function isRegexThreshold({
  actions, services, threshold,
}: {
  actions: SchemaInstance['actions'];
  services: Services;
  threshold: Threshold;
}) {
  const leftAction = actions[threshold.left.actionId];
  const leftAttr = isActionOfType(leftAction, ActionType.SERVICE)
    ? services[leftAction.service.internalName]
      .attributes[threshold.left.attributeId]
    : null;

  if (['in', 'nin'].includes(threshold.operator)
    && (
      (isActionOfType(leftAction, ActionType.SERVICE) && leftAttr?.dataType === 'string')
      || (isActionOfType(leftAction, ActionType.INPUT) && leftAction.input.dataType === 'string')
      || (isActionOfType(leftAction, ActionType.OUTPUT) && leftAction.output.dataType === 'string')
    )
  ) {
    return true;
  }

  return false;
}

export function isThresholdRightAttributeValueNull({
  leftDataType, operator, rightAttributeValue,
}: {
  leftDataType: string;
  operator: string;
  rightAttributeValue: string | number | null;
}) {
  if (!['e', 'ne'].includes(operator)) return false;

  switch (leftDataType) {
    case 'decimal':
    case 'integer':
      return rightAttributeValue === null;
    case 'string':
      return rightAttributeValue === '';
    default:
      return false;
  }
}

export function isThresholdUsingNullOperator({
  leftDataType,
  threshold,
  supportsBestEffortParsing,
}: {
  leftDataType: string;
  threshold: PopulatedThreshold;
  supportsBestEffortParsing: boolean;
}) {
  const {
    operator,
    left: { action: { type: leftActionType } },
    right: { attributeValue: rightAttributeValue },
  } = threshold;

  if (!supportsBestEffortParsing) {
    // by default, only supports Input int/decimal null values
    return leftActionType === ActionType.INPUT
      && ['decimal', 'integer'].includes(leftDataType)
      && isThresholdRightAttributeValueNull({
        leftDataType, operator, rightAttributeValue,
      });
  }

  if (!supportedNullActionTypes.has(leftActionType)) return false;

  const isStringAttributeWithValues = leftDataType === 'string'
    && Boolean(threshold.left.attribute?.values);
  if (isStringAttributeWithValues) return false;

  return isThresholdRightAttributeValueNull({
    leftDataType, operator, rightAttributeValue,
  });
}

/**
 * Returns a string placeholder for any empty or default strings
 * if feature flag is off, any empty string -> '...'
 * best effort parsing default string (multi-select) -> 'make_selection'
 * best effort parsing default string -> 'enter_string'
 */
export function getThresholdPlaceholderForDefaultString({
  threshold,
  supportsBestEffortParsing,
}: {
  threshold: PopulatedThreshold;
  supportsBestEffortParsing: boolean;
}) {
  const {
    left: { attribute: leftAttribute },
    right: { attributeValue: rightAttributeValue },
  } = threshold;

  if (!supportsBestEffortParsing) {
    return rightAttributeValue === ''
      ? PLACEHOLDER_STRING : rightAttributeValue;
  }

  if (rightAttributeValue !== BEST_EFFORT_PARSING_DEFAULT_STRING) {
    return null;
  }

  if (leftAttribute?.values) {
    return BEST_EFFORT_PARSING_PLACEHOLDER_MULTISELECT;
  }

  return BEST_EFFORT_PARSING_PLACEHOLDER_STRING;
}

/**
 * If featureFlag.allowBestEffortRuleEvaluations is on:
 * Returns right attribute value if none of the criteria are met
 * Returns placeholder text if using default string
 * Returns null/not null if using null/not null operator
 * Else returns the threshold right value
 */
export function getThresholdRightValueBasedOnBestEffortParsing({
  actions, reasonCodeGroups, services, threshold, featureFlags,
}: {
  actions: SchemaInstance['actions'];
  reasonCodeGroups: WorkflowProps['reasonCodeGroups'];
  services: WorkflowProps['services'];
  threshold: Threshold;
  featureFlags: Partial<FeatureFlags>;
}) {
  if (!['e', 'ne'].includes(threshold.operator)) {
    return threshold.right.attributeValue;
  }

  const populatedThreshold = populateThresholdData({
    actions, reasonCodeGroups, services, threshold,
  });

  const leftDataType = getThresholdLeftDataType(populatedThreshold);

  if (isThresholdUsingNullOperator({
    leftDataType,
    threshold: populatedThreshold,
    supportsBestEffortParsing: featureFlags.allowBestEffortRuleEvaluations,
  })) {
    return 'null';
  }

  if (leftDataType === 'string') {
    const placeholderText = getThresholdPlaceholderForDefaultString({
      threshold: populatedThreshold,
      supportsBestEffortParsing: featureFlags.allowBestEffortRuleEvaluations,
    });

    return placeholderText || threshold.right.attributeValue;
  }

  return threshold.right.attributeValue;
}


export function indexThresholdsByPublicId(
  thresholds: Threshold[],
): Record<Threshold['publicId'], Threshold> {
  return Object.fromEntries(
    thresholds.map(threshold => [threshold.publicId, threshold]),
  );
}

export function containsReasonCodeGroup(threshold: Threshold): boolean {
  const reasonCodeGroup = threshold.right?.reasonCodeGroup ?? null;
  const reasonCodeGroupId = threshold.right?.reasonCodeGroupId ?? null;

  // guard against undefined and `0`
  return reasonCodeGroup !== null && reasonCodeGroupId !== null;
}


export function getThresholdsForAction(args: {
  action: Action;
  thresholds: Record<Threshold['publicId'], Threshold>;
}): Threshold[] {
  const { action, thresholds } = args;

  const actionThresholds = Object.values(thresholds)
    .filter(threshold => threshold.actionId === action.publicId);

  return actionThresholds;
}
